import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, MessageCircle, Phone, MapPin, Wrench, Sparkles, Shield, Clock, CheckCircle2 } from "lucide-react";
import {
  BUSINESS,
  whatsappLink,
  HERO_IMAGE,
  PROCESS_IMAGE,
  SERVICE_TIERS,
  MATERIALS,
  BEFORE_AFTER_PROJECTS,
  SHOP_WORK_IMAGES,
} from "@/lib/business";
import Marquee from "@/components/Marquee";
import SectionHeader from "@/components/SectionHeader";

const Hero = () => (
  <section
    data-testid="hero-section"
    className="relative min-h-[92vh] flex items-end overflow-hidden"
  >
    <div className="absolute inset-0">
      <img
        src={HERO_IMAGE}
        alt="Shiny red sports car"
        className="w-full h-full object-cover object-center scale-105"
      />
      <div className="absolute inset-0 bg-gradient-to-r from-black via-black/80 to-black/30" />
      <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-black/40" />
      <div className="absolute inset-0 grid-bg opacity-40" />
    </div>

    <div className="relative max-w-[1400px] mx-auto w-full px-6 md:px-10 pb-16 md:pb-24 pt-32">
      <div className="max-w-4xl">
        <div className="flex items-center gap-3 mb-6 animate-fade-up">
          <span className="block w-12 h-[2px] bg-[#E60000]" />
          <span className="font-display text-xs tracking-[0.5em] text-[#FFCC00]">
            110 NORTH SOUND RD • CAYMAN
          </span>
        </div>

        <h1
          data-testid="hero-headline"
          className="font-display text-[14vw] sm:text-7xl md:text-8xl lg:text-9xl leading-[0.85] text-white animate-fade-up"
          style={{ animationDelay: "0.1s" }}
        >
          PAINT.
          <br />
          <span className="text-[#E60000]">PRECISION.</span>
          <br />
          <span className="text-[#FFCC00]">POWER.</span>
        </h1>

        <p
          className="mt-8 text-lg md:text-xl text-[#d4d4d8] max-w-2xl leading-relaxed animate-fade-up"
          style={{ animationDelay: "0.2s" }}
        >
          The1Autospray delivers professional automotive paint &amp; body refinishing —
          single-stage, basecoat/clearcoat, and full restoration with showroom-grade
          finishes.
        </p>

        <div
          className="mt-10 flex flex-wrap gap-4 animate-fade-up"
          style={{ animationDelay: "0.3s" }}
        >
          <Link
            to="/contact"
            data-testid="hero-quote-cta"
            className="group inline-flex items-center gap-3 bg-[#E60000] hover:bg-[#FF1A1A] text-white font-display tracking-widest text-sm px-8 py-4 transition-all duration-300 hover:-translate-y-1"
          >
            GET A QUOTE
            <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
          </Link>
          <a
            href={whatsappLink()}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="hero-whatsapp-cta"
            className="group inline-flex items-center gap-3 bg-transparent border border-white hover:border-[#FFCC00] hover:text-[#FFCC00] text-white font-display tracking-widest text-sm px-8 py-4 transition-all duration-300 hover:-translate-y-1"
          >
            <MessageCircle className="w-4 h-4" />
            WHATSAPP US
          </a>
        </div>

        <div
          className="mt-14 grid grid-cols-2 md:grid-cols-4 gap-6 max-w-3xl animate-fade-up"
          style={{ animationDelay: "0.4s" }}
        >
          {[
            { v: "10+", l: "YEARS EXPERIENCE" },
            { v: "1000+", l: "VEHICLES PAINTED" },
            { v: "2 STAGE", l: "BASECOAT/CLEAR" },
            { v: "INSURANCE", l: "DOCUMENTED" },
          ].map((s) => (
            <div key={s.l} className="border-l-2 border-[#E60000] pl-4">
              <div className="font-display text-3xl text-white">{s.v}</div>
              <div className="text-[10px] tracking-[0.3em] text-[#d4d4d8] mt-1">
                {s.l}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  </section>
);

const ServicesBento = () => {
  const cards = [
    {
      icon: <Sparkles className="w-6 h-6" />,
      title: "Single-Stage Repaints",
      price: "$1,800 – $3,500",
      desc: "Affordable full repaints with prep, body work, and rust treatment.",
      span: "md:col-span-2",
      tone: "yellow",
    },
    {
      icon: <Shield className="w-6 h-6" />,
      title: "Basecoat / Clearcoat",
      price: "$3,500 – $10,000",
      desc: "Premium two-stage finish for showroom-grade depth and durability.",
      span: "md:col-span-2 md:row-span-2",
      tone: "red",
      featured: true,
      image: PROCESS_IMAGE,
    },
    {
      icon: <Wrench className="w-6 h-6" />,
      title: "Panel & Touch-Ups",
      price: "From $150",
      desc: "Hood, fender, bumper, door, roof, trunk, headlight refinishing.",
      span: "md:col-span-2",
      tone: "yellow",
    },
    {
      icon: <Clock className="w-6 h-6" />,
      title: "Fast Turnaround",
      price: "1–10 Days",
      desc: "Light jobs in as little as 1 day. Heavy restorations 7–10 days.",
      span: "md:col-span-2",
      tone: "red",
    },
  ];

  return (
    <section className="relative bg-black py-24 md:py-32">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-16">
          <SectionHeader
            kicker="WHAT WE DO"
            title="BUILT FOR EVERY VEHICLE."
            description="From a single fender refresh to a full custom basecoat/clearcoat restoration — every job is documented, prepped, and finished with precision."
          />
          <Link
            to="/services"
            className="inline-flex items-center gap-2 font-display text-xs tracking-widest text-[#FFCC00] hover:text-white border-b border-[#FFCC00] hover:border-white pb-1 self-start"
            data-testid="home-services-link"
          >
            ALL SERVICES <ArrowRight className="w-3 h-3" />
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 md:gap-6">
          {cards.map((c) => (
            <div
              key={c.title}
              className={`relative overflow-hidden border border-[#27272A] bg-[#0a0a0a] p-8 group hover:border-[#E60000] transition-all duration-300 ${c.span}`}
              data-testid={`bento-card-${c.title.replace(/\s+/g, "-").toLowerCase()}`}
            >
              {c.image && (
                <div className="absolute inset-0">
                  <img
                    src={c.image}
                    alt=""
                    className="w-full h-full object-cover opacity-30 group-hover:opacity-50 group-hover:scale-105 transition-all duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-tr from-black via-black/70 to-transparent" />
                </div>
              )}
              <div className="relative">
                <div
                  className={`inline-flex items-center justify-center w-12 h-12 mb-6 ${
                    c.tone === "red" ? "bg-[#E60000]" : "bg-[#FFCC00] text-black"
                  } text-white`}
                >
                  {c.icon}
                </div>
                <h3 className="font-display text-2xl md:text-3xl text-white leading-tight">
                  {c.title}
                </h3>
                <div
                  className={`mt-2 font-display text-xl ${
                    c.tone === "red" ? "text-[#E60000]" : "text-[#FFCC00]"
                  }`}
                >
                  {c.price}
                </div>
                <p className="mt-4 text-[#d4d4d8] leading-relaxed max-w-md">
                  {c.desc}
                </p>
                {c.featured && (
                  <span className="absolute top-0 right-0 -translate-y-2 translate-x-2 bg-[#FFCC00] text-black font-display text-[10px] tracking-widest px-3 py-1">
                    MOST CHOSEN
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const ProcessSection = () => {
  const steps = [
    {
      n: "01",
      title: "Inspection & Estimate",
      desc: "Physical inspection. Documented scope. Transparent pricing for body work, materials, and labor.",
    },
    {
      n: "02",
      title: "Prep & Body Work",
      desc: "Dent repair, rust treatment, body filler, sanding, and masking — the foundation of a flawless finish.",
    },
    {
      n: "03",
      title: "Paint Application",
      desc: "Single-stage or basecoat/clearcoat applied in a controlled booth with precision spray technique.",
    },
    {
      n: "04",
      title: "Finish & Detail",
      desc: "Cure, polish, and final inspection. Vehicle returned showroom-ready with photo documentation.",
    },
  ];

  return (
    <section className="relative py-24 md:py-32 stripe-bg">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <SectionHeader
          kicker="OUR PROCESS"
          title="FROM ROUGH TO REFINED."
          description="Every vehicle that enters our booth follows the same disciplined four-stage process."
        />

        <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-[#27272A] border border-[#27272A]">
          {steps.map((s) => (
            <div
              key={s.n}
              className="bg-[#0a0a0a] p-8 hover:bg-[#111] transition-colors duration-300"
            >
              <div className="font-display text-6xl text-[#E60000]">{s.n}</div>
              <h3 className="mt-6 font-display text-2xl text-white">{s.title}</h3>
              <p className="mt-3 text-[#d4d4d8] leading-relaxed text-sm">
                {s.desc}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const MaterialsStrip = () => (
  <section className="bg-black border-t border-b border-[#27272A]">
    <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-14">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
        <div>
          <div className="font-display text-xs tracking-[0.4em] text-[#FFCC00] mb-2">
            MATERIALS WE USE
          </div>
          <h3 className="font-display text-3xl md:text-4xl text-white">
            PRO-GRADE.<br />PERIOD.
          </h3>
        </div>
        <Link
          to="/services"
          className="inline-flex items-center gap-2 font-display text-xs tracking-widest text-[#FFCC00] hover:text-white border-b border-[#FFCC00] hover:border-white pb-1 self-start"
        >
          FULL BREAKDOWN <ArrowRight className="w-3 h-3" />
        </Link>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-px bg-[#27272A] border border-[#27272A]">
        {MATERIALS.map((m, i) => (
          <div
            key={m.name}
            className="bg-[#0a0a0a] p-5 hover:bg-[#111] transition-colors group"
          >
            <div className="flex items-baseline gap-3">
              <span className="font-display text-sm text-[#E60000]">
                {String(i + 1).padStart(2, "0")}
              </span>
              <div className="font-display text-lg text-white tracking-wide">
                {m.name.toUpperCase()}
              </div>
            </div>
            <p className="mt-2 text-xs text-[#d4d4d8] leading-relaxed">
              {m.desc}
            </p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const TiersPreview = () => (
  <section className="bg-[#050505] py-24 md:py-32">
    <div className="max-w-[1400px] mx-auto px-6 md:px-10">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-16">
        <SectionHeader
          kicker="SERVICE TIERS"
          title="PICK YOUR FINISH."
          description="Four tiers for every budget and condition. Final pricing confirmed after a physical inspection."
        />
        <Link
          to="/pricing"
          className="inline-flex items-center gap-2 font-display text-xs tracking-widest text-[#FFCC00] hover:text-white border-b border-[#FFCC00] hover:border-white pb-1 self-start"
          data-testid="home-pricing-link"
        >
          FULL PRICING <ArrowRight className="w-3 h-3" />
        </Link>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-[#27272A] border border-[#27272A]">
        {SERVICE_TIERS.map((t) => (
          <div
            key={t.id}
            className={`relative bg-[#0a0a0a] p-8 hover:bg-[#111] transition-colors ${
              t.featured ? "lg:scale-y-[1.02]" : ""
            }`}
            data-testid={`tier-card-${t.id}`}
          >
            {t.featured && (
              <span className="absolute top-0 left-0 bg-[#E60000] text-white font-display text-[10px] tracking-widest px-3 py-1">
                PREMIUM
              </span>
            )}
            <div
              className={`font-display text-xs tracking-[0.3em] ${
                t.accent === "red" ? "text-[#E60000]" : "text-[#FFCC00]"
              }`}
            >
              {t.eta}
            </div>
            <h3 className="mt-3 font-display text-2xl text-white leading-tight min-h-[3.5rem]">
              {t.title}
            </h3>
            <div className="mt-4 font-display text-3xl text-white">{t.price}</div>
            <p className="mt-3 text-sm text-[#d4d4d8] leading-relaxed">{t.blurb}</p>
            <ul className="mt-5 space-y-2">
              {t.bullets.map((b) => (
                <li key={b} className="flex items-start gap-2 text-sm text-[#d4d4d8]">
                  <CheckCircle2
                    className={`w-4 h-4 mt-0.5 shrink-0 ${
                      t.accent === "red" ? "text-[#E60000]" : "text-[#FFCC00]"
                    }`}
                  />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const GalleryPreview = () => {
  const featured = BEFORE_AFTER_PROJECTS[0];
  return (
    <section className="bg-black py-24 md:py-32">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-12">
          <SectionHeader kicker="REAL CUSTOMER WORK" title="BEFORE & AFTER." />
          <Link
            to="/gallery"
            className="inline-flex items-center gap-2 font-display text-xs tracking-widest text-[#FFCC00] hover:text-white border-b border-[#FFCC00] hover:border-white pb-1 self-start"
            data-testid="home-gallery-link"
          >
            VIEW FULL GALLERY <ArrowRight className="w-3 h-3" />
          </Link>
        </div>

        {featured && (
          <div className="border border-[#27272A] bg-[#0a0a0a]">
            <div className="grid grid-cols-1 md:grid-cols-2">
              <div className="relative overflow-hidden aspect-[4/3] border-b md:border-b-0 md:border-r border-[#27272A]">
                <img
                  src={featured.before}
                  alt={`${featured.title} before`}
                  className="w-full h-full object-cover"
                />
                <span className="absolute top-4 left-4 bg-[#E60000] text-white font-display text-xs tracking-[0.3em] px-3 py-1.5">
                  BEFORE
                </span>
              </div>
              <div className="relative overflow-hidden aspect-[4/3]">
                <img
                  src={featured.after}
                  alt={`${featured.title} after`}
                  className="w-full h-full object-cover"
                />
                <span className="absolute top-4 left-4 bg-[#FFCC00] text-black font-display text-xs tracking-[0.3em] px-3 py-1.5">
                  AFTER
                </span>
              </div>
            </div>
            <div className="p-6 md:p-8 border-t border-[#27272A]">
              <h3 className="font-display text-xl md:text-2xl text-white">
                {featured.title}
              </h3>
              <p className="mt-2 text-sm md:text-base text-[#d4d4d8] leading-relaxed">
                {featured.summary}
              </p>
            </div>
          </div>
        )}

        {/* Small strip of real shop work below */}
        <div className="mt-6 grid grid-cols-2 gap-2 md:gap-3 auto-rows-[140px] md:auto-rows-[180px]">
          {SHOP_WORK_IMAGES.map((img, i) => (
            <div
              key={i}
              className="relative overflow-hidden border border-[#27272A] group"
            >
              <img
                src={img.url}
                alt={img.alt}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
              <span className="absolute bottom-3 left-3 bg-black/80 text-white font-display text-[10px] tracking-widest px-2.5 py-1">
                {img.label}
              </span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const ContactCTA = () => (
  <section className="relative bg-[#0a0a0a] py-24 md:py-32 border-t border-[#27272A]">
    <div className="absolute inset-0 stripe-bg opacity-50" />
    <div className="relative max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
      <div>
        <div className="font-display text-xs tracking-[0.4em] text-[#FFCC00] mb-4">
          READY WHEN YOU ARE
        </div>
        <h2 className="font-display text-5xl md:text-6xl lg:text-7xl text-white leading-[0.95]">
          GET YOUR
          <br />
          <span className="text-[#E60000]">QUOTE</span> TODAY.
        </h2>
        <p className="mt-6 text-[#d4d4d8] text-lg max-w-lg">
          Send us a message on WhatsApp or fill out our quick quote form. We
          respond fast.
        </p>
        <div className="mt-8 flex flex-wrap gap-4">
          <Link
            to="/contact"
            data-testid="cta-quote-link"
            className="inline-flex items-center gap-3 bg-[#FFCC00] hover:bg-white text-black font-display tracking-widest text-sm px-8 py-4 transition-all duration-300 hover:-translate-y-1"
          >
            REQUEST QUOTE <ArrowRight className="w-4 h-4" />
          </Link>
          <a
            href={whatsappLink()}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="cta-whatsapp-link"
            className="inline-flex items-center gap-3 bg-[#E60000] hover:bg-[#FF1A1A] text-white font-display tracking-widest text-sm px-8 py-4 transition-all duration-300 hover:-translate-y-1"
          >
            <MessageCircle className="w-4 h-4" /> WHATSAPP
          </a>
        </div>
      </div>

      <div className="border border-[#27272A] bg-[#050505] p-8 md:p-10">
        <div className="font-display text-xs tracking-[0.4em] text-[#FFCC00] mb-4">
          VISIT THE SHOP
        </div>
        <div className="space-y-5 text-white">
          <div className="flex items-start gap-4">
            <MapPin className="w-5 h-5 text-[#E60000] mt-0.5" />
            <div>
              <div className="font-display text-lg">{BUSINESS.location}</div>
              <div className="text-sm text-[#d4d4d8]">Cayman Islands</div>
            </div>
          </div>
          <div className="flex items-start gap-4">
            <Phone className="w-5 h-5 text-[#E60000] mt-0.5" />
            <div>
              <div className="font-display text-lg">{BUSINESS.whatsapp}</div>
              <div className="text-sm text-[#d4d4d8]">WhatsApp Direct</div>
            </div>
          </div>
          <div className="flex items-start gap-4">
            <MessageCircle className="w-5 h-5 text-[#E60000] mt-0.5" />
            <a
              href={`mailto:${BUSINESS.email}`}
              className="font-display text-lg text-white hover:text-[#FFCC00] break-all"
            >
              {BUSINESS.email}
            </a>
          </div>
        </div>
      </div>
    </div>
  </section>
);

export const Home = () => (
  <div data-testid="home-page">
    <Hero />
    <Marquee
      items={[
        "FULL PAINT JOBS",
        "BASECOAT / CLEARCOAT",
        "BODY WORK",
        "RUST REPAIR",
        "PANEL REFINISH",
        "CUSTOM & CLASSIC",
      ]}
    />
    <GalleryPreview />
    <ServicesBento />
    <ProcessSection />
    <MaterialsStrip />
    <ContactCTA />
  </div>
);

export default Home;
